/**
 * Buyer profile → RFQ contact/company fields (same source as generic logistics RFQ).
 * GET /company/my-profile + session user; only fills blank fields.
 */
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';

/** Normalize GET /company/my-profile response (handles `{ data }` from sendSuccess). */
export function tradeCompanyProfileFromResponse(resp) {
  if (!resp || typeof resp !== 'object') return null;
  const pick = (o) =>
    o &&
    typeof o === 'object' &&
    (o.company_id != null ||
      o.company_name != null ||
      (o.contact_details && typeof o.contact_details === 'object'))
      ? o
      : null;
  return pick(resp) || pick(resp.data) || pick(resp.data?.data) || null;
}

export function strTrim(v) {
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

function formatRegisteredAddress(addr) {
  if (!addr || typeof addr !== 'object') return '';
  const line1 = [addr.address, addr.landmark].filter((x) => x && String(x).trim()).join(', ');
  const line2 = [addr.city, addr.state, addr.pincode].filter((x) => x != null && String(x).trim() !== '').join(', ');
  const parts = [line1, line2, addr.country].filter((x) => x && String(x).trim());
  return parts.join(', ').trim();
}

function pickContactPhone(profile, storedUser, contactDetails) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const su = storedUser && typeof storedUser === 'object' ? storedUser : {};
  const cd = contactDetails && typeof contactDetails === 'object' ? contactDetails : {};
  return (
    strTrim(p.user_phone) ||
    strTrim(su.phone) ||
    strTrim(su.mobile) ||
    strTrim(su.contact_number) ||
    strTrim(cd.contact_number) ||
    strTrim(cd.alternate_contact_number) ||
    strTrim(cd.contact_phone) ||
    strTrim(cd.contactPhone) ||
    strTrim(cd.mobile)
  );
}

/**
 * Contact person, email, phone from user_login (my-profile JOIN) then session user, then company contact_details.
 */
function pickUserLoginContact(profile, storedUser) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const su = storedUser && typeof storedUser === 'object' ? storedUser : {};
  const cd = p.contact_details && typeof p.contact_details === 'object' ? p.contact_details : {};
  return {
    contactPerson:
      strTrim(p.user_name) ||
      strTrim(su.name) ||
      strTrim(cd.contact_name) ||
      strTrim(cd.alternate_contact_name),
    email:
      strTrim(p.user_email) ||
      strTrim(su.email) ||
      strTrim(cd.contact_email) ||
      strTrim(cd.alternate_email),
    contactNumber: pickContactPhone(p, su, cd),
  };
}

/** Map company profile + session user → reference & company block fields. */
export function mapBuyerProfileToRefCompanyFields(profile, storedUser) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const su = storedUser && typeof storedUser === 'object' ? storedUser : {};
  const contact = pickUserLoginContact(profile, storedUser);

  let companyAddress = formatRegisteredAddress(p.address);
  if (!companyAddress) {
    const a = strTrim(su.address);
    const pc = strTrim(su.pincode);
    companyAddress = [a, pc].filter(Boolean).join(', ').trim();
  }

  const mapped = {
    companyName: strTrim(p.company_name) || strTrim(su.company_name),
    email: contact.email,
    contactNumber: contact.contactNumber,
    contactPerson: contact.contactPerson,
    companyAddress,
    companyWebsite: strTrim(p.website),
  };

  const hasAnything =
    mapped.companyName ||
    mapped.email ||
    mapped.contactNumber ||
    mapped.contactPerson ||
    mapped.companyAddress ||
    mapped.companyWebsite;
  return hasAnything ? mapped : null;
}

export async function fetchBuyerRefCompanyFieldsForRfqPrefill() {
  if (!StorageService.getToken()) return null;
  const storedUser = StorageService.getUser();
  try {
    const resp = await CommonService.get('/company/my-profile');
    const profile = tradeCompanyProfileFromResponse(resp);
    return mapBuyerProfileToRefCompanyFields(profile, storedUser);
  } catch {
    return mapBuyerProfileToRefCompanyFields(null, storedUser);
  }
}

function fieldIsBlank(prev, key) {
  const v = prev[key];
  return v == null || String(v).trim() === '';
}

/**
 * @param {'generic' | 'business-relocation' | 'exhibition-logistics'} variant
 */
export function mergeBuyerPrefillIntoForm(prev, mapped, variant) {
  if (!mapped) return prev;
  const next = { ...prev };
  const merge = (key, val) => {
    if (!fieldIsBlank(prev, key)) return;
    if (val != null && String(val).trim() !== '') {
      next[key] = val;
    }
  };
  merge('companyName', mapped.companyName);
  merge('contactPerson', mapped.contactPerson);
  merge('contactNumber', mapped.contactNumber);
  if (variant === 'generic') {
    merge('email', mapped.email);
    merge('companyAddress', mapped.companyAddress);
    merge('companyWebsite', mapped.companyWebsite);
  } else if (variant === 'business-relocation' || variant === 'exhibition-logistics') {
    merge('companyEmail', mapped.email);
  }
  return next;
}

const normLoc = (value) => strTrim(value).toLowerCase();

function mapMasterList(response, idKeys, labelKeys) {
  if (!response?.success || !response.data) return [];
  const raw = Array.isArray(response.data)
    ? response.data
    : response.data.states ||
      response.data.cities ||
      response.data.countries ||
      response.data.master_states ||
      response.data.master_cities ||
      response.data.master_countries ||
      [];
  return raw
    .map((row) => {
      let id;
      let label;
      for (const k of idKeys) {
        if (row[k] != null && row[k] !== '') {
          id = row[k];
          break;
        }
      }
      for (const k of labelKeys) {
        if (row[k] != null && String(row[k]).trim() !== '') {
          label = row[k];
          break;
        }
      }
      return { id, label };
    })
    .filter((o) => o.id != null && o.label);
}

function matchLocationOption(options, value) {
  const v = strTrim(value);
  if (!v || !Array.isArray(options) || !options.length) return '';
  const byId = options.find((o) => String(o.id) === v);
  if (byId) return String(byId.id);
  const byLabel = options.find((o) => normLoc(o.label) === normLoc(v));
  return byLabel ? String(byLabel.id) : '';
}

async function fetchMasterCountries() {
  const response = await CommonService.get('/master/company-Country', {}, { silent: true });
  return mapMasterList(response, ['country_id', 'id', 'value'], [
    'country_name',
    'country_label',
    'label',
    'name',
    'country',
  ]);
}

async function fetchMasterStates(countryId) {
  const response = await CommonService.get(`/master/company-State/${countryId}`, {}, { silent: true });
  return mapMasterList(response, ['state_id', 'id', 'value'], [
    'state_name',
    'state_label',
    'label',
    'name',
    'state',
  ]);
}

async function fetchMasterCities(stateId) {
  const response = await CommonService.get(`/master/company-City/${stateId}`, {}, { silent: true });
  return mapMasterList(response, ['city_id', 'id', 'value'], [
    'city_name',
    'city_label',
    'label',
    'name',
    'city',
  ]);
}

function pickRegisteredAddressParts(profile, storedUser) {
  const p = profile && typeof profile === 'object' ? profile : {};
  const su = storedUser && typeof storedUser === 'object' ? storedUser : {};
  const addr = p.address && typeof p.address === 'object' ? p.address : {};

  const country = strTrim(addr.country);
  const state = strTrim(addr.state);
  const city = strTrim(addr.city);
  const pincode = strTrim(addr.pincode) || strTrim(su.pincode);
  let street = [addr.address, addr.landmark]
    .map((x) => strTrim(x))
    .filter(Boolean)
    .join(', ');

  if (!street) {
    street = strTrim(su.address);
  }

  if (!country && !state && !city && !pincode && !street) {
    return null;
  }

  return { country, state, city, pincode, street };
}

/** Resolve company registered address → Business Relocation current location (master IDs). */
export async function fetchBuyerCurrentLocationPrefillForBusinessRelocation() {
  if (!StorageService.getToken()) return null;
  const storedUser = StorageService.getUser();
  let profile = null;
  try {
    const resp = await CommonService.get('/company/my-profile', {}, { silent: true });
    profile = tradeCompanyProfileFromResponse(resp);
  } catch {
    profile = null;
  }

  const parts = pickRegisteredAddressParts(profile, storedUser);
  if (!parts) return null;

  let currentCountry = '';
  let currentState = '';
  let currentCity = '';

  if (parts.country) {
    const countries = await fetchMasterCountries();
    currentCountry = matchLocationOption(countries, parts.country);
    if (currentCountry) {
      const states = await fetchMasterStates(currentCountry);
      currentState = matchLocationOption(states, parts.state);
      if (currentState) {
        const cities = await fetchMasterCities(currentState);
        currentCity = matchLocationOption(cities, parts.city);
      }
    }
  }

  const patch = {
    currentCountry,
    currentState,
    currentCity,
    currentPincode: parts.pincode,
    currentAddress: parts.street,
  };

  const hasAnything = Object.values(patch).some((v) => strTrim(v));
  return hasAnything ? patch : null;
}

export function mergeBusinessRelocationLocationPrefill(prev, patch) {
  if (!patch) return prev;
  const next = { ...prev };
  const keys = [
    'currentCountry',
    'currentState',
    'currentCity',
    'currentPincode',
    'currentAddress',
  ];
  keys.forEach((key) => {
    if (!fieldIsBlank(prev, key)) return;
    const val = patch[key];
    if (val != null && strTrim(val)) {
      next[key] = String(val);
    }
  });
  return next;
}

/** Same registered address → exhibition organization location fields. */
export function mergeExhibitionOrgLocationPrefill(prev, patch) {
  if (!patch) return prev;
  const mapped = {
    orgCountry: patch.currentCountry,
    orgState: patch.currentState,
    orgCity: patch.currentCity,
    orgPincode: patch.currentPincode,
    orgAddress: patch.currentAddress,
  };
  const next = { ...prev };
  Object.entries(mapped).forEach(([key, val]) => {
    const prevVal = prev[key];
    if (prevVal != null && String(prevVal).trim() !== '') return;
    if (val != null && strTrim(val)) {
      next[key] = String(val);
    }
  });
  return next;
}
